import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/// <reference types="@types/google.maps" />
import { memo, forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState, } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from '@eruvaka/web-pondlogs-i18n';
import { env, colorTokens, AppInput } from '@eruvaka/web-pondlogs-base';
import { fetchPondsWithGeometry, fetchLatestDoAndTemp, createPond, activatePond, deletePond, updatePond, toggleTestPond, toggleAlerts, } from '../api/pondsApi';
import { useLocations, createLocation } from '../hooks/useLocations';
import { loadGoogleMapsScript, getFeaturePolygon, getLatLng, captureUserLocation, } from '../lib/mapUtils';
import { Maps } from '../lib/Maps';
import { useMapState } from '../hooks/useMapState';
import { MapControls } from './map/MapControls';
import { DrawPondPanel } from './map/DrawPondPanel';
import { PondDetailSidePanel } from './map/PondDetailSidePanel';
import { PondHoverCard } from './map/PondHoverCard';
import { DeletePondDialog } from './DeletePondDialogNew';
const SPECIES = ['Vannamei', 'Monodon', 'Fish', 'Other'];
const FieldRow = ({ children }) => {
    return _jsx(Box, { sx: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }, children: children });
};
const Field = ({ label, children }) => {
    return (_jsxs(Box, { children: [_jsx(Typography, { sx: {
                    fontSize: 11,
                    fontWeight: 600,
                    color: colorTokens.primary,
                    mb: 0.5,
                    display: 'block',
                }, children: label }), children] }));
};
const inputSx = {
    width: '100%',
    px: 1.25,
    py: 0.85,
    borderRadius: '10px',
    fontSize: 12,
    border: `1px solid ${colorTokens.gray200}`,
    outline: 'none',
    color: colorTokens.gray800,
    bgcolor: colorTokens.white,
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    '&:focus': { borderColor: colorTokens.primary },
    '&:disabled': { bgcolor: colorTokens.gray50, color: colorTokens.gray600 },
};
const Toggle = ({ checked, onChange }) => {
    return (_jsx(Box, { component: "button", type: "button", onClick: () => onChange(!checked), sx: {
            width: 36,
            height: 20,
            borderRadius: '10px',
            border: 'none',
            cursor: 'pointer',
            bgcolor: checked ? colorTokens.info : colorTokens.gray300,
            position: 'relative',
            flexShrink: 0,
            transition: 'background 0.2s',
            p: 0,
        }, children: _jsx(Box, { sx: {
                position: 'absolute',
                top: 3,
                left: checked ? 19 : 3,
                width: 14,
                height: 14,
                borderRadius: '50%',
                bgcolor: 'white',
                transition: 'left 0.2s',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            } }) }));
};
const PondMapViewInner = (props, ref) => {
    const { locationId, onPondClick, onPondSaved, onPondActivate, hideDrawPanel, onPolygonDrawn, onPolygonPlaceResolved, } = props;
    const mapDivRef = useRef(null);
    const mapsRef = useRef(null);
    const searchInputRef = useRef(null);
    // Always holds the latest callbacks so drawing callbacks never go stale
    const onPolygonDrawnRef = useRef(onPolygonDrawn);
    const onPolygonPlaceResolvedRef = useRef(onPolygonPlaceResolved);
    // Refs used inside attachDataListeners to avoid recreating the callback on every state change
    const activatingPondRef = useRef(null);
    const boundaryEditingPondRef = useRef(null);
    const isDrawPanelOpenRef = useRef(false);
    useEffect(() => {
        onPolygonDrawnRef.current = onPolygonDrawn;
    }, [onPolygonDrawn]);
    useEffect(() => {
        onPolygonPlaceResolvedRef.current = onPolygonPlaceResolved;
    }, [onPolygonPlaceResolved]);
    const [pondsIndex, setPondsIndex] = useState(new Map());
    const [mapsReady, setMapsReady] = useState(false);
    const [isDrawing, setIsDrawing] = useState(false);
    const [boundaryEditingPond, setBoundaryEditingPond] = useState(null);
    const [activatingPond, setActivatingPond] = useState(null);
    // Keep refs in sync with state for use inside stable callbacks
    useEffect(() => {
        activatingPondRef.current = activatingPond;
    }, [activatingPond]);
    useEffect(() => {
        boundaryEditingPondRef.current = boundaryEditingPond;
    }, [boundaryEditingPond]);
    const [deletingPond, setDeletingPond] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [boundaryEditName, setBoundaryEditName] = useState('');
    const [boundaryEditStockingDate, setBoundaryEditStockingDate] = useState('');
    const [boundaryEditPlsStocked, setBoundaryEditPlsStocked] = useState('');
    const [boundaryEditAeratorHp, setBoundaryEditAeratorHp] = useState('');
    const [boundaryEditHatcheryName, setBoundaryEditHatcheryName] = useState('');
    const [boundaryEditSpecies, setBoundaryEditSpecies] = useState('Vannamei');
    const [boundaryEditIsTestPond, setBoundaryEditIsTestPond] = useState(false);
    const [boundaryEditAlertsEnabled, setBoundaryEditAlertsEnabled] = useState(true);
    const [boundaryEditError, setBoundaryEditError] = useState(null);
    const [boundaryEditSaving, setBoundaryEditSaving] = useState(false);
    const [activateStockingDate, setActivateStockingDate] = useState('');
    const [activatePlsStocked, setActivatePlsStocked] = useState('');
    const [activateInitialAbw, setActivateInitialAbw] = useState('');
    const [activateAeratorHp, setActivateAeratorHp] = useState('');
    const [activateHatcheryName, setActivateHatcheryName] = useState('');
    const [activateSpecies, setActivateSpecies] = useState('Vannamei');
    const [activatePondSize, setActivatePondSize] = useState('');
    const [activateFieldErrors, setActivateFieldErrors] = useState({});
    const [activateError, setActivateError] = useState(null);
    const [activateSaving, setActivateSaving] = useState(false);
    // Keeps the DrawPondPanel mounted across all 3 onboarding steps, even if mapMode
    // is reset by loadPondsToMap or other side effects after pond creation.
    const [isPondOnboardingActive, setIsPondOnboardingActive] = useState(false);
    const [hoverCard, setHoverCard] = useState(null);
    const hoverHideTimerRef = useRef(null);
    const { t } = useTranslation('main');
    const { locations } = useLocations();
    /** Derives a human-readable name from reverse-geocoded place info */
    const deriveLocationName = (place) => place?.city ?? place?.district ?? place?.state ?? '';
    const { mapMode, selectedPond, drawnCoordinates, drawnAreaHa, drawnPlace, setMapMode, setSelectedPond, setHoverPond, setDrawnPolygon, setDrawnPlace, resetDraw, } = useMapState();
    /** Returns an existing location ID by name, or creates a new one */
    const handleResolveLocationId = useCallback(async (locationName) => {
        const existing = locations.find((l) => l.name.toLowerCase() === locationName.toLowerCase());
        if (existing)
            return existing.id;
        const created = await createLocation({
            name: locationName,
            city: drawnPlace?.city ?? undefined,
            state: drawnPlace?.state ?? undefined,
            country: drawnPlace?.country ?? undefined,
            pincode: drawnPlace?.pincode ?? undefined,
        });
        return created.id;
    }, [locations, drawnPlace]);
    // Keeping locationId in a ref makes loadPondsToMap stable (empty useCallback deps),
    // which prevents the double-load that occurred when locationId transitioned from
    // undefined → a real value and caused the effect to re-fire twice.
    const locationIdRef = useRef(locationId);
    useEffect(() => {
        locationIdRef.current = locationId;
    }, [locationId]);
    const loadPondsToMap = useCallback(async (maps) => {
        const lid = locationIdRef.current;
        try {
            const [activePonds, inactivePonds, doData] = await Promise.all([
                fetchPondsWithGeometry({ locationId: lid, status: 'active', limit: 200 }),
                fetchPondsWithGeometry({ locationId: lid, status: 'inactive', limit: 200 }),
                lid ? fetchLatestDoAndTemp(lid) : Promise.resolve([]),
            ]);
            const ponds = [...activePonds, ...inactivePonds];
            const doIndex = new Map(doData.map((d) => [d.pondId, d]));
            const features = ponds
                .filter((p) => p.geometry?.coordinates?.[0]?.length)
                .map((p) => {
                const do_ = doIndex.get(p.id);
                return getFeaturePolygon(p.geometry.coordinates[0], p.id, {
                    status: p.status,
                    name: p.name,
                    doValue: do_?.doValue ?? null,
                    criticalLowerLimit: do_?.criticalLowerLimit ?? 3,
                    lowerLimit: do_?.lowerLimit ?? 4,
                });
            });
            maps.setMapData(features);
            maps.setOptions('DATA');
            // Render pond name labels at polygon centroids
            maps.setPondLabels(features.map((f) => {
                const status = f.getProperty('status');
                const doValue = f.getProperty('doValue');
                const criticalLower = f.getProperty('criticalLowerLimit') ?? 3;
                const lower = f.getProperty('lowerLimit') ?? 4;
                let color = '#00ACA9';
                if (!status || status.toUpperCase() !== 'ACTIVE') {
                    color = '#dcdcdc';
                }
                else if (doValue != null && doValue < criticalLower) {
                    color = '#C8102E';
                }
                else if (doValue != null && doValue < lower) {
                    color = '#FBBA00';
                }
                return {
                    // eslint-disable-next-line no-undef
                    position: f.getProperty('center'),
                    name: String(f.getProperty('name') ?? ''),
                    color,
                };
            }));
            setLoadError(null);
            const index = new Map(ponds.map((p) => [
                p.id,
                {
                    ...p,
                    doValue: doIndex.get(p.id)?.doValue ?? null,
                    tempValue: doIndex.get(p.id)?.tempValue ?? null,
                    criticalLowerLimit: doIndex.get(p.id)?.criticalLowerLimit ?? 3,
                    lowerLimit: doIndex.get(p.id)?.lowerLimit ?? 4,
                },
            ]));
            setPondsIndex(index);
            // Fit map to show all pond polygons
            const pondsWithGeometry = ponds.filter((p) => p.geometry?.coordinates?.[0]?.length);
            if (pondsWithGeometry.length > 0) {
                // eslint-disable-next-line no-undef
                const bounds = new google.maps.LatLngBounds();
                pondsWithGeometry.forEach((p) => {
                    p.geometry.coordinates[0].forEach(([lng, lat]) => bounds.extend({ lat, lng }));
                });
                maps.map.fitBounds(bounds, 60);
                return;
            }
        }
        catch (err) {
            setLoadError(err instanceof Error ? err.message : 'Failed to load ponds');
        }
        // No ponds — centre on user's location or a default
        try {
            const pos = await captureUserLocation();
            maps.setMapCenter(getLatLng({ lat: pos.coords.latitude, lng: pos.coords.longitude }));
        }
        catch {
            maps.setMapCenter(getLatLng({ lat: 0.777259, lng: 91.142578 }));
        }
    }, []);
    // Pan the map so the pond sits in the left visible area when a right-side panel opens.
    // rightPad should match the panel width + its right offset (≈480px for 460px panels).
    const panToPond = useCallback((pond, rightPad = 480) => {
        const maps = mapsRef.current;
        if (!maps)
            return;
        const coords = pond.geometry?.coordinates?.[0];
        if (!coords?.length)
            return;
        // eslint-disable-next-line no-undef
        const bounds = new google.maps.LatLngBounds();
        coords.forEach(([lng, lat]) => bounds.extend({ lat, lng }));
        maps.map.fitBounds(bounds, { top: 80, right: rightPad, bottom: 80, left: 20 });
    }, []);
    const attachDataListeners = useCallback((maps, index) => {
        maps.addMapDataListeners({
            // eslint-disable-next-line no-undef
            onClick: (feature) => {
                // Block pond selection while onboarding or any panel is active
                if (isDrawPanelOpenRef.current ||
                    activatingPondRef.current ||
                    boundaryEditingPondRef.current)
                    return;
                const id = String(feature.getId());
                const pond = index.get(id);
                if (!pond)
                    return;
                setSelectedPond(pond);
                setMapMode('SHOW_DETAILS_OF_POND');
                const bounds = feature.getProperty('bounds');
                // Pad the right side for the detail side panel (~300px)
                if (bounds)
                    maps.map.fitBounds(bounds, {
                        top: 80,
                        right: 320,
                        bottom: 80,
                        left: 20,
                    });
                if (onPondClick)
                    onPondClick(pond);
            },
            // eslint-disable-next-line no-undef
            onHover: (feature, x, y) => {
                // Suppress hover during onboarding, or for the pond that is currently being activated/edited
                if (isDrawPanelOpenRef.current)
                    return;
                const id = String(feature.getId());
                if (activatingPondRef.current?.id === id || boundaryEditingPondRef.current?.id === id)
                    return;
                const pond = index.get(id);
                if (!pond)
                    return;
                if (hoverHideTimerRef.current)
                    clearTimeout(hoverHideTimerRef.current);
                setHoverCard({ pond, clientX: x, clientY: y });
                setHoverPond(pond);
            },
            onMouseOut: () => {
                hoverHideTimerRef.current = setTimeout(() => setHoverCard(null), 160);
                setHoverPond(null);
            },
        });
    }, [onPondClick, setHoverPond, setMapMode, setSelectedPond]);
    // Re-attach listeners whenever the pond index updates
    useEffect(() => {
        if (mapsRef.current && mapsReady) {
            attachDataListeners(mapsRef.current, pondsIndex);
        }
    }, [attachDataListeners, mapsReady, pondsIndex]);
    useEffect(() => {
        if (!activatingPond)
            return;
        setActivateStockingDate('');
        setActivatePlsStocked('');
        setActivateInitialAbw('');
        setActivateAeratorHp(activatingPond.aeratorHp != null ? String(activatingPond.aeratorHp) : '');
        setActivateHatcheryName(activatingPond.hatcheryName ?? '');
        setActivateSpecies(activatingPond.speciesType ?? 'Vannamei');
        setActivatePondSize(activatingPond.areaHectares != null && Number.isFinite(activatingPond.areaHectares)
            ? String(activatingPond.areaHectares)
            : '');
        setActivateError(null);
        setActivateFieldErrors({});
    }, [activatingPond]);
    // Pan map when activate panel opens so the pond is visible left of the panel
    useEffect(() => {
        if (!activatingPond)
            return;
        const fullPond = pondsIndex.get(activatingPond.id);
        if (fullPond)
            panToPond(fullPond);
    }, [activatingPond, pondsIndex, panToPond]);
    // Pan map when boundary-edit panel opens
    useEffect(() => {
        if (boundaryEditingPond)
            panToPond(boundaryEditingPond);
    }, [boundaryEditingPond, panToPond]);
    // Reload ponds when the map becomes ready or when locationId changes.
    // loadPondsToMap is stable (empty deps, uses locationIdRef), so including
    // locationId here directly ensures exactly ONE load per locationId value.
    useEffect(() => {
        if (!mapsReady || !mapsRef.current)
            return;
        void loadPondsToMap(mapsRef.current);
    }, [mapsReady, locationId, loadPondsToMap]);
    // Bootstrap Google Maps on mount — just initialise the map, do NOT load ponds
    // here. The effect above fires once mapsReady becomes true and handles the
    // initial data load with the current locationId.
    useEffect(() => {
        const div = mapDivRef.current;
        if (!div)
            return;
        void loadGoogleMapsScript(env.googleMapsApiKey, ['places', 'geometry']).then(() => {
            const maps = new Maps(div);
            mapsRef.current = maps;
            maps.setOptions('MAP');
            setMapMode('INITIAL');
            maps.setMapListener();
            if (searchInputRef.current) {
                maps.initAutoComplete(searchInputRef.current, () => undefined);
            }
            setMapsReady(true);
        });
        return () => {
            mapsRef.current?.removeAllListeners();
        };
    }, []);
    const handleDraw = () => {
        const maps = mapsRef.current;
        if (!maps)
            return;
        setMapMode('DRAWING');
        setIsDrawing(true);
        setBoundaryEditingPond(null);
        resetDraw();
        // Clear any previously drawn (unsaved) polygon before starting a new draw session
        maps.clearDrawnPolygon();
        maps.setDrawingControl((coords, areaHa, centreStr) => {
            setDrawnPolygon(coords, areaHa);
            setIsDrawing(false);
            setMapMode('DRAW_POND_COMPLETE');
            // Notify parent immediately — area and coordinates are ready before geocoding
            if (hideDrawPanel && onPolygonDrawnRef.current) {
                onPolygonDrawnRef.current(coords, areaHa, {});
            }
            // Geocode asynchronously; notify parent with place info for location auto-fill
            if (centreStr) {
                void maps.reverseGeocode(centreStr).then((place) => {
                    setDrawnPlace(place);
                    if (hideDrawPanel && onPolygonPlaceResolvedRef.current) {
                        onPolygonPlaceResolvedRef.current(place);
                    }
                });
            }
        });
    };
    const handleStartBoundaryEdit = (pond) => {
        const maps = mapsRef.current;
        if (!maps)
            return;
        // Close any open activation panel before entering edit mode
        if (activatingPond) {
            mapsRef.current?.highlightPond(null);
            setActivatingPond(null);
            setActivateError(null);
            setActivateFieldErrors({});
        }
        setHoverCard(null);
        setSelectedPond(null);
        setBoundaryEditingPond(pond);
        panToPond(pond);
        setMapMode('EDITING');
        setIsDrawing(false);
        setDrawnPlace({});
        setBoundaryEditError(null);
        setBoundaryEditName(pond.name);
        setBoundaryEditStockingDate(pond.cultivationDate ? new Date(pond.cultivationDate).toISOString().split('T')[0] : '');
        setBoundaryEditPlsStocked(pond.plStocked ? String(pond.plStocked) : '');
        setBoundaryEditAeratorHp(pond.aeratorHp != null ? String(pond.aeratorHp) : '');
        setBoundaryEditHatcheryName(pond.hatcheryName ?? '');
        setBoundaryEditSpecies(pond.speciesType ?? 'Vannamei');
        setBoundaryEditIsTestPond(Boolean(pond.isTest));
        setBoundaryEditAlertsEnabled(!pond.pauseAlerts);
        const started = maps.startEditBoundary(pond.id, (coords, areaHa) => {
            setDrawnPolygon(coords, areaHa);
        });
        if (!started) {
            setBoundaryEditingPond(null);
            setMapMode('INITIAL');
            setLoadError('Unable to edit pond boundary. Please try again.');
            return;
        }
        setLoadError(null);
    };
    const handleCancelDraw = () => {
        const maps = mapsRef.current;
        maps?.clearDrawnPolygon();
        if (maps && mapsReady) {
            attachDataListeners(maps, pondsIndex);
        }
        setBoundaryEditingPond(null);
        setBoundaryEditError(null);
        setIsPondOnboardingActive(false);
        isDrawPanelOpenRef.current = false;
        setMapMode('INITIAL');
        setIsDrawing(false);
        resetDraw();
    };
    const handleSaveBoundaryEdit = async () => {
        if (!boundaryEditingPond || !drawnCoordinates || !drawnAreaHa)
            return;
        setBoundaryEditSaving(true);
        setBoundaryEditError(null);
        try {
            const updates = {
                areaHectares: drawnAreaHa,
                boundaryCoordinates: drawnCoordinates,
            };
            if (boundaryEditName.trim() && boundaryEditName.trim() !== boundaryEditingPond.name) {
                updates.name = boundaryEditName.trim();
            }
            const originalStockingDate = boundaryEditingPond.cultivationDate
                ? new Date(boundaryEditingPond.cultivationDate).toISOString().split('T')[0]
                : '';
            if (boundaryEditStockingDate && boundaryEditStockingDate !== originalStockingDate) {
                updates.cultivationDate = boundaryEditStockingDate;
            }
            const parsedPls = boundaryEditPlsStocked.trim() === '' ? null : Number(boundaryEditPlsStocked);
            if (parsedPls != null && Number.isFinite(parsedPls) && parsedPls >= 0) {
                if (parsedPls !== (boundaryEditingPond.plStocked ?? 0)) {
                    updates.plStocked = parsedPls;
                }
            }
            const parsedAeratorHp = boundaryEditAeratorHp.trim() === '' ? null : Number(boundaryEditAeratorHp);
            if (parsedAeratorHp != null && Number.isFinite(parsedAeratorHp) && parsedAeratorHp >= 0) {
                if (parsedAeratorHp !== (boundaryEditingPond.aeratorHp ?? null)) {
                    updates.aeratorHp = parsedAeratorHp;
                }
            }
            const hatcheryName = boundaryEditHatcheryName.trim();
            if (hatcheryName !== (boundaryEditingPond.hatcheryName ?? '')) {
                updates.hatcheryName = hatcheryName || undefined;
            }
            updates.speciesType = boundaryEditSpecies;
            await updatePond(boundaryEditingPond.id, updates);
            const currentIsTest = Boolean(boundaryEditingPond.isTest);
            if (boundaryEditIsTestPond !== currentIsTest) {
                await toggleTestPond(boundaryEditingPond.id, boundaryEditIsTestPond);
            }
            const currentPauseAlerts = Boolean(boundaryEditingPond.pauseAlerts);
            const nextPauseAlerts = !boundaryEditAlertsEnabled;
            if (nextPauseAlerts !== currentPauseAlerts) {
                await toggleAlerts(boundaryEditingPond.id, nextPauseAlerts);
            }
            mapsRef.current?.clearDrawnPolygon();
            setBoundaryEditingPond(null);
            setMapMode('INITIAL');
            resetDraw();
            if (mapsRef.current)
                await loadPondsToMap(mapsRef.current);
        }
        catch (err) {
            setBoundaryEditError(err instanceof Error ? err.message : 'Failed to save pond');
        }
        finally {
            setBoundaryEditSaving(false);
        }
    };
    const handleSavePond = async (payload) => {
        const pond = await createPond(payload);
        // Lock the panel open before any async side-effects (map reload, parent
        // callbacks) that could change mapMode and unmount the panel mid-flow.
        setIsPondOnboardingActive(true);
        isDrawPanelOpenRef.current = true;
        // Clear the editable drawn polygon — the saved pond will appear as a
        // non-editable data layer feature so the boundary cannot be changed in step 2/3.
        mapsRef.current?.clearDrawnPolygon();
        if (mapsRef.current)
            await loadPondsToMap(mapsRef.current);
        // Highlight the newly created pond so it stands out in step 2
        mapsRef.current?.highlightPond(pond.id);
        if (onPondSaved)
            onPondSaved(pond);
        return pond;
    };
    const handleCancelActivate = () => {
        mapsRef.current?.highlightPond(null);
        setActivatingPond(null);
        setActivateError(null);
        setActivateFieldErrors({});
    };
    const handleDeleteRequest = (pond) => {
        setHoverCard(null);
        setBoundaryEditingPond(null);
        setActivatingPond(null);
        setDeletingPond(pond);
        setDeleteDialogOpen(true);
    };
    const handleDeleteConfirm = async (_pondId, confirmationText) => {
        if (!deletingPond)
            return;
        await deletePond(deletingPond.id, confirmationText);
        if (selectedPond?.id === deletingPond.id) {
            setSelectedPond(null);
        }
        setDeleteDialogOpen(false);
        setDeletingPond(null);
        if (mapsRef.current)
            await loadPondsToMap(mapsRef.current);
    };
    const handleSaveActivate = async () => {
        if (!activatingPond)
            return;
        const fieldErrors = {};
        if (!activateStockingDate)
            fieldErrors.stockingDate = 'onboarding.activatePonds.errorStockingDateRequired';
        if (!activatePlsStocked || Number(activatePlsStocked) <= 0)
            fieldErrors.plsStocked = 'onboarding.activatePonds.errorPlsStockedRequired';
        if (!activateInitialAbw)
            fieldErrors.initialAbw = 'onboarding.activatePonds.errorInitialAbwRequired';
        else if (Number(activateInitialAbw) <= 0)
            fieldErrors.initialAbw = 'onboarding.activatePonds.errorInitialAbwInvalid';
        if (Object.keys(fieldErrors).length > 0) {
            setActivateFieldErrors(fieldErrors);
            return;
        }
        setActivateFieldErrors({});
        const pls = Number(activatePlsStocked);
        setActivateSaving(true);
        setActivateError(null);
        try {
            await activatePond(activatingPond.id, {
                stockingDate: activateStockingDate,
                plsStocked: Math.trunc(pls),
                initialAbw: activateInitialAbw.trim() === '' || !Number.isFinite(Number(activateInitialAbw))
                    ? undefined
                    : Number(activateInitialAbw),
                aeratorHp: activateAeratorHp.trim() === '' || !Number.isFinite(Number(activateAeratorHp))
                    ? undefined
                    : Number(activateAeratorHp),
                hatcheryName: activateHatcheryName.trim() || undefined,
                speciesType: activateSpecies,
                pondSize: activatePondSize.trim() === '' || !Number.isFinite(Number(activatePondSize))
                    ? undefined
                    : Number(activatePondSize),
            });
            if (onPondActivate)
                onPondActivate(activatingPond);
            mapsRef.current?.highlightPond(null);
            setActivatingPond(null);
            if (mapsRef.current)
                await loadPondsToMap(mapsRef.current);
        }
        catch {
            setActivateError('onboarding.activatePonds.errorActivationFailed');
        }
        finally {
            setActivateSaving(false);
        }
    };
    const handleLocate = () => {
        void captureUserLocation().then((pos) => {
            mapsRef.current?.setMapCenter(getLatLng({ lat: pos.coords.latitude, lng: pos.coords.longitude }));
            mapsRef.current?.map.setZoom(17);
        });
    };
    useImperativeHandle(ref, () => ({
        startDraw: handleDraw,
        savePond: async (name, locationIdArg, units = 'hectares') => {
            if (!drawnCoordinates || !drawnAreaHa)
                return;
            await handleSavePond({
                title: name,
                pond_coordinates: drawnCoordinates,
                timezone: { name: 'Asia/Kolkata', offset: 5.5 },
                location_id: locationIdArg,
                size: drawnAreaHa,
                units,
            });
        },
        resetDraw: handleCancelDraw,
    }));
    const isDrawPanelOpen = mapMode === 'DRAWING' || mapMode === 'DRAW_POND_COMPLETE' || isPondOnboardingActive;
    // Keep ref in sync for stable callbacks (attachDataListeners) that can't take isDrawPanelOpen as a dep
    useEffect(() => {
        isDrawPanelOpenRef.current = isDrawPanelOpen;
    }, [isDrawPanelOpen]);
    // Dim all existing pond polygons while the DrawPondPanel is open so the user
    // focuses on the pond being created. Restores normal style when panel closes.
    useEffect(() => {
        if (!mapsRef.current || !mapsReady)
            return;
        mapsRef.current.setDimMode(isDrawPanelOpen);
    }, [isDrawPanelOpen, mapsReady]);
    const rightOffset = selectedPond ? 312 : 12;
    return (_jsxs(Box, { sx: { position: 'relative', width: '100%', height: '100%', minHeight: 480 }, children: [_jsx(Box, { sx: {
                    position: 'absolute',
                    top: 12,
                    left: 12,
                    zIndex: 10,
                    width: { xs: 'calc(100% - 180px)', sm: 320 },
                }, children: _jsx(AppInput, { inputRef: searchInputRef, placeholder: "Search place\u2026", slotProps: {
                        input: {
                            startAdornment: (_jsx(InputAdornment, { position: "start", children: _jsx(SearchIcon, { fontSize: "small" }) })),
                            sx: { bgcolor: 'rgba(255,255,255,0.95)' },
                        },
                    } }) }), !activatingPond && (_jsx(Box, { sx: { position: 'absolute', top: 12, right: rightOffset, zIndex: 10 }, children: _jsx(MapControls, { mapMode: mapMode, onDraw: handleDraw, onLocate: handleLocate }) })), isDrawPanelOpen && !hideDrawPanel && !boundaryEditingPond && (_jsx(Box, { sx: { position: 'absolute', top: 72, right: rightOffset, zIndex: 10 }, children: _jsx(DrawPondPanel, { coordinates: drawnCoordinates, areaHa: drawnAreaHa, place: drawnPlace, locationOptions: locations, defaultLocationName: drawnPlace
                        ? deriveLocationName(drawnPlace)
                        : (locations.find((l) => l.id === locationId)?.name ?? ''), onResolveLocationId: handleResolveLocationId, isDrawing: isDrawing, onStartDraw: handleDraw, onSave: handleSavePond, onCancel: handleCancelDraw }) })), mapMode === 'EDITING' && boundaryEditingPond && (_jsxs(Box, { sx: {
                    position: 'absolute',
                    top: 72,
                    bottom: 86,
                    right: rightOffset,
                    zIndex: 20,
                    width: 460,
                    maxWidth: 'calc(100% - 24px)',
                    bgcolor: 'background.paper',
                    borderRadius: '16px',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 25px 50px -12px rgba(0, 0, 0, 0.12)',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                }, children: [_jsxs(Box, { sx: {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: 2.25,
                            py: 1.5,
                            bgcolor: colorTokens.gray50,
                            borderBottom: `1px solid ${colorTokens.gray200}`,
                        }, children: [_jsxs(Box, { children: [_jsx(Typography, { sx: { fontSize: 13, fontWeight: 800, color: colorTokens.primary }, children: "Edit Pond Boundary" }), _jsx(Typography, { sx: { fontSize: 11, color: colorTokens.gray600, mt: 0.25 }, children: boundaryEditingPond.name })] }), _jsx(Box, { component: "button", type: "button", onClick: handleCancelDraw, sx: {
                                    p: 0.75,
                                    border: 'none',
                                    bgcolor: 'transparent',
                                    cursor: 'pointer',
                                    borderRadius: 1,
                                    display: 'flex',
                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' },
                                }, children: _jsx(Typography, { sx: { fontSize: 15, color: colorTokens.gray600, lineHeight: 1 }, children: "x" }) })] }), _jsxs(Box, { sx: { p: 2.25, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1.5 }, children: [_jsx(Typography, { sx: { fontSize: 12, color: colorTokens.gray600 }, children: "Move polygon points to update coordinates." }), _jsxs(FieldRow, { children: [_jsx(Field, { label: "Pond Name", children: _jsx(Box, { component: "input", value: boundaryEditName, onChange: (e) => setBoundaryEditName(e.target.value), sx: inputSx }) }), _jsx(Field, { label: "Pond Size (ha)", children: _jsx(Box, { component: "input", value: drawnAreaHa ? drawnAreaHa.toFixed(2) : '', sx: inputSx, disabled: true }) })] }), _jsxs(FieldRow, { children: [_jsx(Field, { label: "Stocking Date", children: _jsx(Box, { component: "input", type: "date", value: boundaryEditStockingDate, onChange: (e) => setBoundaryEditStockingDate(e.target.value), sx: inputSx }) }), _jsx(Field, { label: "PLs Stocked", children: _jsx(Box, { component: "input", type: "number", value: boundaryEditPlsStocked, onChange: (e) => setBoundaryEditPlsStocked(e.target.value), min: 0, sx: inputSx }) })] }), _jsxs(FieldRow, { children: [_jsx(Field, { label: "Aerator HP", children: _jsx(Box, { component: "input", type: "number", value: boundaryEditAeratorHp, onChange: (e) => setBoundaryEditAeratorHp(e.target.value), min: 0, step: 0.5, sx: inputSx }) }), _jsx(Field, { label: "Hatchery Name", children: _jsx(Box, { component: "input", value: boundaryEditHatcheryName, onChange: (e) => setBoundaryEditHatcheryName(e.target.value), sx: inputSx }) })] }), _jsx(Field, { label: "Species", children: _jsx(Box, { component: "select", value: boundaryEditSpecies, onChange: (e) => setBoundaryEditSpecies(e.target.value), sx: { ...inputSx, appearance: 'auto' }, children: SPECIES.map((s) => (_jsx("option", { value: s, children: s }, s))) }) }), _jsxs(Box, { children: [_jsx(Typography, { sx: {
                                            fontSize: 10,
                                            fontWeight: 800,
                                            color: colorTokens.gray600,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.07em',
                                            mb: 0.75,
                                        }, children: "Pond Settings" }), _jsxs(Box, { sx: {
                                            borderRadius: '10px',
                                            border: `1px solid ${colorTokens.gray200}`,
                                            overflow: 'hidden',
                                        }, children: [_jsxs(Box, { sx: {
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    px: 1.5,
                                                    py: 1.15,
                                                    bgcolor: colorTokens.gray50,
                                                }, children: [_jsx(Box, { sx: { fontSize: 12, fontWeight: 700, color: colorTokens.gray800 }, children: "Test Pond" }), _jsx(Toggle, { checked: boundaryEditIsTestPond, onChange: setBoundaryEditIsTestPond })] }), _jsx(Box, { sx: { height: 1, bgcolor: colorTokens.gray200 } }), _jsxs(Box, { sx: {
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    px: 1.5,
                                                    py: 1.15,
                                                    bgcolor: colorTokens.gray50,
                                                }, children: [_jsx(Box, { sx: { fontSize: 12, fontWeight: 700, color: colorTokens.gray800 }, children: "Enable Alerts" }), _jsx(Toggle, { checked: boundaryEditAlertsEnabled, onChange: setBoundaryEditAlertsEnabled })] })] })] }), boundaryEditError && (_jsx(Box, { sx: {
                                    p: 1.25,
                                    borderRadius: '10px',
                                    bgcolor: `${colorTokens.error}10`,
                                    border: `1px solid ${colorTokens.error}30`,
                                }, children: _jsx(Typography, { sx: { fontSize: 12, color: colorTokens.error }, children: boundaryEditError }) }))] }), _jsxs(Box, { sx: {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: 1.25,
                            px: 2.25,
                            py: 1.5,
                            borderTop: `1px solid ${colorTokens.gray200}`,
                            bgcolor: colorTokens.gray50,
                        }, children: [_jsx(Box, { component: "button", type: "button", onClick: handleCancelDraw, disabled: boundaryEditSaving, sx: {
                                    px: 2,
                                    py: 1,
                                    borderRadius: '10px',
                                    border: `1px solid ${colorTokens.gray200}`,
                                    bgcolor: 'white',
                                    color: colorTokens.gray600,
                                    fontSize: 12,
                                    fontWeight: 600,
                                    lineHeight: 1.2,
                                    cursor: boundaryEditSaving ? 'not-allowed' : 'pointer',
                                    opacity: boundaryEditSaving ? 0.7 : 1,
                                }, children: "Cancel" }), _jsx(Box, { component: "button", type: "button", onClick: () => void handleSaveBoundaryEdit(), disabled: !drawnCoordinates || !drawnAreaHa || boundaryEditSaving, sx: {
                                    px: 2.5,
                                    py: 1,
                                    borderRadius: '10px',
                                    border: 'none',
                                    bgcolor: !drawnCoordinates || !drawnAreaHa || boundaryEditSaving
                                        ? colorTokens.gray300
                                        : colorTokens.primary,
                                    color: 'white',
                                    fontSize: 12,
                                    fontWeight: 600,
                                    lineHeight: 1.2,
                                    cursor: !drawnCoordinates || !drawnAreaHa || boundaryEditSaving
                                        ? 'not-allowed'
                                        : 'pointer',
                                }, children: boundaryEditSaving ? 'Saving…' : 'Save Changes' })] })] })), _jsx(Box, { ref: mapDivRef, sx: { width: '100%', height: '100%', minHeight: 480 } }), loadError && (_jsx(Box, { sx: {
                    position: 'absolute',
                    bottom: 72,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 20,
                    bgcolor: 'error.main',
                    color: 'white',
                    borderRadius: 2,
                    px: 2,
                    py: 1,
                    fontSize: 12,
                    fontWeight: 600,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                    maxWidth: 360,
                    textAlign: 'center',
                }, children: loadError })), hoverCard && mapDivRef.current && (_jsx(PondHoverCard, { pond: hoverCard.pond, clientX: hoverCard.clientX, clientY: hoverCard.clientY, containerRect: mapDivRef.current.getBoundingClientRect(), onEdit: (pond) => {
                    handleStartBoundaryEdit(pond);
                    setHoverCard(null);
                }, onActivate: (pond) => {
                    // Cancel any active boundary edit on the maps side before switching
                    if (boundaryEditingPond) {
                        mapsRef.current?.clearDrawnPolygon();
                        setBoundaryEditError(null);
                        setMapMode('INITIAL');
                        resetDraw();
                    }
                    setBoundaryEditingPond(null);
                    setActivatingPond(pond);
                    setHoverCard(null);
                    mapsRef.current?.highlightPond(pond.id);
                }, onDelete: handleDeleteRequest, onMouseEnter: () => {
                    if (hoverHideTimerRef.current)
                        clearTimeout(hoverHideTimerRef.current);
                }, onMouseLeave: () => {
                    hoverHideTimerRef.current = setTimeout(() => setHoverCard(null), 160);
                } })), _jsx(PondDetailSidePanel, { pond: selectedPond, onClose: () => setSelectedPond(null) }), deletingPond && (_jsx(DeletePondDialog, { open: deleteDialogOpen, pond: deletingPond, onClose: () => {
                    setDeleteDialogOpen(false);
                    setDeletingPond(null);
                }, onConfirm: handleDeleteConfirm })), activatingPond && (_jsxs(Box, { sx: {
                    position: 'absolute',
                    top: 72,
                    bottom: 86,
                    right: rightOffset,
                    zIndex: 20,
                    width: 460,
                    maxWidth: 'calc(100% - 24px)',
                    bgcolor: 'background.paper',
                    borderRadius: '16px',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 25px 50px -12px rgba(0, 0, 0, 0.12)',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                }, children: [_jsxs(Box, { sx: {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: 2.25,
                            py: 1.5,
                            bgcolor: colorTokens.gray50,
                            borderBottom: `1px solid ${colorTokens.gray200}`,
                        }, children: [_jsxs(Box, { children: [_jsx(Typography, { sx: { fontSize: 13, fontWeight: 800, color: colorTokens.primary }, children: t('onboarding.activatePonds.activateButton') }), _jsx(Typography, { sx: { fontSize: 11, color: colorTokens.gray600, mt: 0.25 }, children: activatingPond.name })] }), _jsx(Box, { component: "button", type: "button", onClick: handleCancelActivate, sx: {
                                    p: 0.75,
                                    border: 'none',
                                    bgcolor: 'transparent',
                                    cursor: 'pointer',
                                    borderRadius: 1,
                                    display: 'flex',
                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' },
                                }, children: _jsx(Typography, { sx: { fontSize: 15, color: colorTokens.gray600, lineHeight: 1 }, children: "x" }) })] }), _jsxs(Box, { sx: { p: 2.25, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1.5 }, children: [_jsxs(FieldRow, { children: [_jsx(Field, { label: "Pond Size (ha)", children: _jsx(Box, { component: "input", type: "number", readOnly: true, value: activatePondSize, sx: {
                                                ...inputSx,
                                                bgcolor: colorTokens.gray50,
                                                color: colorTokens.gray500,
                                                cursor: 'default',
                                            } }) }), _jsx(Field, { label: "Species", children: _jsx(Box, { component: "select", value: activateSpecies, onChange: (e) => setActivateSpecies(e.target.value), sx: { ...inputSx, appearance: 'auto' }, children: SPECIES.map((s) => (_jsx("option", { value: s, children: s }, s))) }) })] }), _jsxs(FieldRow, { children: [_jsxs(Field, { label: `${t('onboarding.activatePonds.stockingDate')} *`, children: [_jsx(Box, { component: "input", type: "date", value: activateStockingDate, onChange: (e) => {
                                                    setActivateStockingDate(e.target.value);
                                                    if (activateFieldErrors.stockingDate)
                                                        setActivateFieldErrors((p) => ({ ...p, stockingDate: undefined }));
                                                }, sx: {
                                                    ...inputSx,
                                                    borderColor: activateFieldErrors.stockingDate ? colorTokens.error : undefined,
                                                } }), activateFieldErrors.stockingDate && (_jsx(Typography, { sx: { fontSize: 10, color: colorTokens.error, mt: 0.25 }, children: t(activateFieldErrors.stockingDate) }))] }), _jsxs(Field, { label: `${t('onboarding.activatePonds.plsStocked')} *`, children: [_jsx(Box, { component: "input", type: "number", value: activatePlsStocked, onChange: (e) => {
                                                    setActivatePlsStocked(e.target.value);
                                                    if (activateFieldErrors.plsStocked)
                                                        setActivateFieldErrors((p) => ({ ...p, plsStocked: undefined }));
                                                }, min: 1, sx: {
                                                    ...inputSx,
                                                    borderColor: activateFieldErrors.plsStocked ? colorTokens.error : undefined,
                                                } }), activateFieldErrors.plsStocked && (_jsx(Typography, { sx: { fontSize: 10, color: colorTokens.error, mt: 0.25 }, children: t(activateFieldErrors.plsStocked) }))] })] }), _jsxs(FieldRow, { children: [_jsxs(Field, { label: t('onboarding.activatePonds.initialAbwLabel'), children: [_jsx(Box, { component: "input", type: "number", value: activateInitialAbw, onChange: (e) => {
                                                    setActivateInitialAbw(e.target.value);
                                                    if (activateFieldErrors.initialAbw)
                                                        setActivateFieldErrors((p) => ({ ...p, initialAbw: undefined }));
                                                }, min: 0, step: 0.01, sx: {
                                                    ...inputSx,
                                                    borderColor: activateFieldErrors.initialAbw ? colorTokens.error : undefined,
                                                } }), activateFieldErrors.initialAbw && (_jsx(Typography, { sx: { fontSize: 10, color: colorTokens.error, mt: 0.25 }, children: t(activateFieldErrors.initialAbw) }))] }), _jsx(Field, { label: "Aerator HP", children: _jsx(Box, { component: "input", type: "number", value: activateAeratorHp, onChange: (e) => setActivateAeratorHp(e.target.value), min: 0, step: 0.5, sx: inputSx }) })] }), _jsx(Field, { label: "Hatchery Name", children: _jsx(Box, { component: "input", value: activateHatcheryName, onChange: (e) => setActivateHatcheryName(e.target.value), sx: inputSx }) }), activateError && (_jsx(Box, { sx: {
                                    p: 1.25,
                                    borderRadius: '10px',
                                    bgcolor: `${colorTokens.error}10`,
                                    border: `1px solid ${colorTokens.error}30`,
                                }, children: _jsx(Typography, { sx: { fontSize: 12, color: colorTokens.error }, children: t(activateError) }) }))] }), _jsxs(Box, { sx: {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: 1.25,
                            px: 2.25,
                            py: 1.5,
                            borderTop: `1px solid ${colorTokens.gray200}`,
                            bgcolor: colorTokens.gray50,
                        }, children: [_jsx(Box, { component: "button", type: "button", onClick: handleCancelActivate, disabled: activateSaving, sx: {
                                    px: 2,
                                    py: 1,
                                    borderRadius: '10px',
                                    border: `1px solid ${colorTokens.gray200}`,
                                    bgcolor: 'white',
                                    color: colorTokens.gray600,
                                    fontSize: 12,
                                    fontWeight: 600,
                                    lineHeight: 1.2,
                                    cursor: activateSaving ? 'not-allowed' : 'pointer',
                                    opacity: activateSaving ? 0.7 : 1,
                                }, children: "Cancel" }), _jsx(Box, { component: "button", type: "button", onClick: () => void handleSaveActivate(), disabled: activateSaving, sx: {
                                    px: 2.5,
                                    py: 1,
                                    borderRadius: '10px',
                                    border: 'none',
                                    bgcolor: activateSaving ? colorTokens.gray300 : colorTokens.primary,
                                    color: 'white',
                                    fontSize: 12,
                                    fontWeight: 600,
                                    lineHeight: 1.2,
                                    cursor: activateSaving ? 'not-allowed' : 'pointer',
                                }, children: activateSaving
                                    ? t('onboarding.common.activatingLabel')
                                    : t('onboarding.activatePonds.activateButton') })] })] })), _jsx(Box, { sx: {
                    position: 'absolute',
                    bottom: 16,
                    left: 16,
                    zIndex: 9,
                    display: 'flex',
                    gap: 1.5,
                    alignItems: 'center',
                    bgcolor: 'rgba(255,255,255,0.92)',
                    borderRadius: 2,
                    px: 2,
                    py: 0.75,
                    boxShadow: '0 1px 6px rgba(0,0,0,0.25)',
                    flexWrap: 'wrap',
                    maxWidth: 'min(640px, calc(100% - 32px))',
                }, children: [
                    { color: colorTokens.blueGrayLight, label: 'Inactive Pond' },
                    { color: colorTokens.orange, label: 'Low DO' },
                    { color: colorTokens.error, label: 'Critical DO' },
                    { color: colorTokens.secondary, label: 'Active Pond' },
                ].map(({ color, label }) => (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.5 }, children: [_jsx(Box, { sx: { width: 14, height: 14, borderRadius: 0.5, bgcolor: color, flexShrink: 0 } }), _jsx(Box, { component: "span", sx: { fontSize: 11, fontWeight: 500, color: 'text.secondary', whiteSpace: 'nowrap' }, children: label })] }, label))) })] }));
};
export const PondMapView = memo(forwardRef(PondMapViewInner));
